import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Icon from '@ant-design/icons';
import { Button, Drawer, Modal, Popover, Radio, Select, Spin, Tooltip } from 'antd';
import { Formik } from 'formik';
import { Form } from 'antd';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { LockKeyhole } from 'lucide-react';
import FormField from 'components/FormField';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useResolveTodoTypeLabel } from 'views/PureLLM/Hooks/useResolveTodoTypeLabel';
import type { ApiReceptionMode, ContentParameter } from '../../../Types/api';
import TriggerConditionList from './TriggerConditionList';
import type { TriggerCondition } from './TriggerConditionList';
import type { AppliedStoreItem } from './AppliedStores';
import TemplateText from 'views/PureLLM/Components/TemplateText';
import { ReactComponent as MessageSvg } from 'views/PureLLM/assets/message.svg';
import { ReactComponent as TodoSvg } from 'views/PureLLM/assets/todo-icon.svg';
import { getOfficialScenarioTodoPolicies } from 'services/api/pureLLM';
import moreSvg from 'views/Aide/assets/more.svg';

export interface ReceptionModeOption {
  value: ApiReceptionMode;
  label: string;
  hint?: string;
}

export interface ReceptionModeFormValues {
  mode: ApiReceptionMode;
  todoType?: string;
}

type TranslateFn = (key: string) => string;

const TODO_REQUIRED_MODES: ApiReceptionMode[] = [
  'AI_REPLY_AND_TODO',
  'AI_REPLY_AND_HUMAN_TODO',
  'NOT_REPLY_AND_TODO',
];

function needTodoType(mode: ApiReceptionMode) {
  return TODO_REQUIRED_MODES.includes(mode);
}

function isReplyMode(mode: ApiReceptionMode) {
  return mode.startsWith('AI_REPLY');
}

export function getReceptionModeOptions(t: TranslateFn): ReceptionModeOption[] {
  return [
    { value: 'AI_REPLY', label: t('domain.aiAgent.receptionStrategy.type.replyOnly') },
    {
      value: 'AI_REPLY_AND_HUMAN',
      label: t('domain.aiAgent.receptionStrategy.type.replyThenTransfer'),
    },
    {
      value: 'AI_REPLY_AND_LOGISTICS',
      label: t('domain.aiAgent.receptionStrategy.type.replyWithLogisticsCard'),
      hint: t('scene.aiAgentConfig.receptionMode.hint.tiktokOnly'),
    },
    {
      value: 'AI_REPLY_AND_LOGISTICS_HUMAN',
      label: t('domain.aiAgent.receptionStrategy.type.replyWithLogisticsCardThenTransfer'),
      hint: t('scene.aiAgentConfig.receptionMode.hint.tiktokOnly'),
    },
    {
      value: 'AI_REPLY_AND_TODO',
      label: t('domain.aiAgent.receptionStrategy.type.replyAndCreateTask'),
    },
    {
      value: 'AI_REPLY_AND_HUMAN_TODO',
      label: t('domain.aiAgent.receptionStrategy.type.replyThenTransferAndCreateTask'),
    },
    {
      value: 'NOT_REPLY',
      label: t('domain.aiAgent.receptionStrategy.type.transferWithoutReply'),
    },
    {
      value: 'NOT_REPLY_AND_TODO',
      label: t('domain.aiAgent.receptionStrategy.type.transferWithoutReplyAndCreateTask'),
    },
    {
      value: 'NOT_REPLY_WAITING',
      label: t('domain.aiAgent.receptionStrategy.type.waitForBuyerFollowUp'),
    },
  ];
}

export function getReceptionModeOptionLabel(
  t: TranslateFn,
  mode: ApiReceptionMode,
  todoType?: string,
  resolveTodoTypeLabel?: (todoType?: string) => string | undefined,
) {
  const option = getReceptionModeOptions(t).find((item) => item.value === mode);
  if (!option) return mode;

  const todoLabel = needTodoType(mode) ? resolveTodoTypeLabel?.(todoType) ?? todoType : undefined;

  return todoLabel ? `${option.label}[${todoLabel}]` : option.label;
}

export interface ReceptionModeDrawerProps {
  visible: boolean;
  onClose: () => void;
  /** 分支名称 */
  branchName: string;
  /** 触发条件 */
  triggerConditions: TriggerCondition[];
  /** 回复逻辑描述 */
  replyLogicDesc: string;
  /** 回复示例 */
  replyExample: string;
  /** 固定回复文本 */
  fixedReplyContent?: string;
  /** 是否为预设回复（固定回复） */
  isFixedReply?: boolean;
  /** 当前接待模式 */
  currentMode: ApiReceptionMode;
  /** 出厂接待模式 */
  originalReceptionMode?: ApiReceptionMode;
  /** 当前关联待办类型 */
  currentTodoType?: string;
  /** 当前店铺组名称 */
  storeGroupName?: string;
  /** 当前分支适用店铺范围 */
  storeScope?: {
    allStore: boolean;
    storeInfo?: AppliedStoreItem[];
  };
  /** 保存回调 */
  onSave?: (values: ReceptionModeFormValues) => Promise<void>;
  /** 点击去修改触发条件 */
  onModifyTrigger?: () => void;
  /** 盖在 Modal 上打开时需大于 Modal 的 z-index */
  zIndex?: number;
  /** 模板参数映射 (name → ContentParameter) */
  parameterMap?: Record<string, ContentParameter>;
}

function getDisplayStoreName(
  store: AppliedStoreItem,
  storeNameConfigMap: ReturnType<typeof useStoreNameConfig>['configMap'],
) {
  const { externalType = '', region = '' } = store;
  const { [externalType]: { [region]: isStoreAliasFirst = true } = {} } = storeNameConfigMap;

  return isStoreAliasFirst ? store.alias || store.storeName || '' : store.storeName || '';
}

function ReceptionModeConfirmStores(props: {
  stores: AppliedStoreItem[];
  storeNameConfigMap: ReturnType<typeof useStoreNameConfig>['configMap'];
}) {
  const { stores, storeNameConfigMap } = props;
  const normalizedStores = useMemo(
    () =>
      stores
        .map((store, index) => ({
          id: store.storeId ?? `${store.storeName ?? store.alias ?? 'store'}-${index}`,
          name: getDisplayStoreName(store, storeNameConfigMap),
        }))
        .filter((store) => Boolean(store.name)),
    [storeNameConfigMap, stores],
  );

  if (normalizedStores.length <= 3) {
    return null;
  }

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      content={
        <ConfirmStoreList>
          {normalizedStores.map((store) => (
            <li key={store.id}>{store.name}</li>
          ))}
        </ConfirmStoreList>
      }
    >
      <ConfirmStoreMoreTrigger type="button">
        <img src={moreSvg} alt="more" />
      </ConfirmStoreMoreTrigger>
    </Popover>
  );
}

function renderStoreListTemplate(template: string, storeListNode: React.ReactNode) {
  const placeholder = '<storelist></storelist>';

  if (!template.includes(placeholder)) {
    return template;
  }

  const [before, after] = template.split(placeholder);

  return (
    <>
      {before}
      {storeListNode}
      {after}
    </>
  );
}

function ReceptionModeDrawer(props: ReceptionModeDrawerProps) {
  const { t } = useTranslate();
  const {
    visible,
    onClose,
    branchName,
    triggerConditions,
    replyLogicDesc,
    replyExample,
    fixedReplyContent,
    isFixedReply = false,
    currentMode,
    originalReceptionMode,
    currentTodoType,
    storeGroupName,
    storeScope,
    onSave,
    onModifyTrigger,
    zIndex,
    parameterMap,
  } = props;

  const disableReplyModes = originalReceptionMode?.includes('NOT_REPLY');
  const resolveTodoTypeLabel = useResolveTodoTypeLabel();
  const { configMap: storeNameConfigMap } = useStoreNameConfig();

  const { data: todoPolicyData, isLoading: todoPolicyLoading } = useQuery({
    queryKey: ['officialScenarioTodoPolicies'],
    queryFn: ({ signal }) => getOfficialScenarioTodoPolicies(signal),
    enabled: visible,
  });

  const receptionModeOptions = useMemo<ReceptionModeOption[]>(
    () => getReceptionModeOptions(t),
    [t],
  );
  const normalizedReplyLogicDesc = replyLogicDesc.trim();
  const normalizedReplyContent = (isFixedReply ? fixedReplyContent : replyExample)?.trim() ?? '';
  const shouldShowReplyTitle = Boolean(normalizedReplyLogicDesc);
  const shouldShowReplyContent = Boolean(normalizedReplyContent);

  const todoOptions = useMemo(() => {
    const options =
      todoPolicyData?.policies?.map((policy) => {
        const value = policy.source === 0 ? String(policy.type) : policy.name;
        const label =
          policy.source === 0
            ? resolveTodoTypeLabel(String(policy.type)) ?? policy.name
            : policy.name;

        return {
          value,
          label,
        };
      }) ?? [];

    if (currentTodoType && !options.some((option) => option.value === currentTodoType)) {
      options.unshift({
        value: currentTodoType,
        label: resolveTodoTypeLabel(currentTodoType) ?? currentTodoType,
      });
    }

    return options;
  }, [currentTodoType, resolveTodoTypeLabel, todoPolicyData]);

  const initialValues: ReceptionModeFormValues = {
    mode: currentMode,
    todoType: currentTodoType,
  };

  const appliedStores = useMemo(() => storeScope?.storeInfo ?? [], [storeScope?.storeInfo]);
  const appliedStoreNames = useMemo(
    () =>
      appliedStores.map((store) => getDisplayStoreName(store, storeNameConfigMap)).filter(Boolean),
    [appliedStores, storeNameConfigMap],
  );
  const visibleStoreNames = appliedStoreNames.slice(0, 3);
  const confirmContent = useMemo(() => {
    if (storeScope?.allStore) {
      return t('scene.aiAgentConfig.receptionModeDialog.save.confirmContent.whenAllStoreTrue', {
        storeGroupName,
      });
    }

    const storeText = visibleStoreNames.map((name) => `[${name}]`).join(' ');

    if (appliedStoreNames.length <= 3) {
      return t(
        'scene.aiAgentConfig.receptionModeDialog.save.confirmContent.whenAllStoreFalse.lessStore',
        {
          stores: storeText,
        },
      );
    }

    return renderStoreListTemplate(
      t('scene.aiAgentConfig.receptionModeDialog.save.confirmContent.whenAllStoreFalse', {
        stores: storeText,
        storeCount: appliedStoreNames.length,
      }),
      <ReceptionModeConfirmStores stores={appliedStores} storeNameConfigMap={storeNameConfigMap} />,
    );
  }, [
    appliedStoreNames.length,
    appliedStores,
    storeGroupName,
    storeNameConfigMap,
    storeScope?.allStore,
    t,
    visibleStoreNames,
  ]);

  const confirmSave = (values: ReceptionModeFormValues) =>
    new Promise<void>((resolve, reject) => {
      const cancelError = new Error('reception-mode-save-cancelled');

      Modal.confirm({
        title: t('scene.aiAgentConfig.receptionModeDialog.save.confirmTitle'),
        content: <ConfirmContent>{confirmContent}</ConfirmContent>,
        okText: t('common.action.confirm'),
        cancelText: t('common.action.cancel'),
        onOk: async () => {
          try {
            if (!onSave) return;
            await onSave(values);
            resolve();
          } catch (error) {
            reject(error);
            throw error;
          }
        },
        onCancel: () => {
          reject(cancelError);
        },
      });
    });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await confirmSave({
            ...values,
            todoType: needTodoType(values.mode) ? values.todoType : '',
          });
          onClose();
        } catch (error) {
          if (!(error instanceof Error && error.message === 'reception-mode-save-cancelled')) {
            throw error;
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, resetForm }) => (
        <Drawer
          data-sid="rmd-drawer-01"
          title={t('scene.aiAgentConfig.receptionModeDialog.title')}
          placement="right"
          width={696}
          visible={visible}
          zIndex={zIndex}
          onClose={() => {
            resetForm();
            onClose();
          }}
          className={drawerStyle}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                data-sid="rmd-cancelbtn-02"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                {t('common.action.cancel')}
              </Button>
              <Button
                data-sid="rmd-savebtn-03"
                type="primary"
                loading={isSubmitting}
                onClick={() => handleSubmit()}
              >
                {t('common.action.save')}
              </Button>
            </div>
          }
        >
          {/* 分支名称 */}
          <div
            data-sid="rmd-section-04"
            className="mb-4 rounded-[4px] p-4"
            style={{
              backgroundImage:
                'linear-gradient(180deg, var(--fett-scenario-header-bg) 0%, var(--fett-bg-panel) 66%)',
              border: '1px solid #E4ECF6',
              boxShadow: 'inset 0 -1px 0 0 #F0F2F5',
            }}
          >
            <div
              data-sid="rmd-label-05"
              className="mb-2"
              style={{ fontWeight: 500, fontSize: 14, color: 'var(--fett-text-primary)' }}
            >
              {t('scene.aiAgentConfig.receptionModeDialog.label.branchName')}
            </div>
            <div data-sid="rmd-name-06" className="text-sm text-[var(--fett-text-primary)]">
              {branchName}
            </div>
          </div>

          {/* 触发条件 */}
          {triggerConditions.length > 0 && (
            <div
              data-sid="rmd-section-07"
              className="mb-4 rounded-[4px] p-4"
              style={{
                backgroundImage:
                  'linear-gradient(180deg, var(--fett-scenario-header-bg) 0%, var(--fett-bg-panel) 66%)',
                border: '1px solid #E4ECF6',
                boxShadow: 'inset 0 -1px 0 0 #F0F2F5',
              }}
            >
              <div
                data-sid="rmd-triggerlabel-08"
                className="flex items-center justify-between mb-2"
              >
                <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--fett-text-primary)' }}>
                  {t('scene.aiAgentConfig.scenarioDetail.label.triggerCondition')}
                </span>
                <div className="flex items-center gap-2">
                  <span
                    data-sid="rmd-systempreset-10"
                    className="inline-flex items-center gap-1"
                    style={{ fontSize: '12px', color: 'var(--fett-text-tertiary)' }}
                  >
                    <LockKeyhole size={12} />
                    {t('scene.aiAgentConfig.receptionModeDialog.label.systemPreset')}
                  </span>
                  {onModifyTrigger && (
                    <Button
                      data-sid="rmd-modifybtn-09"
                      type="link"
                      className="!p-0 !h-auto"
                      onClick={onModifyTrigger}
                    >
                      {t('scene.aiAgentConfig.receptionModeDialog.action.goToUpdate')}
                    </Button>
                  )}
                </div>
              </div>
              <TriggerConditionList
                conditions={triggerConditions}
                operatorDataSid="rmd-triggerop-22"
                itemDataSid="rmd-triggeritem-23"
                tagDataSid="rmd-triggertag-11"
                overflowTriggerDataSid="rmd-trigmore-24"
                overflowPopoverDataSid="rmd-trigpopover-25"
              />
            </div>
          )}

          {/* 选择接待模式 */}
          <div
            data-sid="rmd-section-12"
            className="rounded-[4px] p-4"
            style={{
              backgroundColor: 'var(--fett-bg-panel)',
              backgroundImage:
                'linear-gradient(180deg, var(--fett-scenario-header-bg) 0%, var(--fett-bg-panel) 66%)',
              backgroundSize: '100% 80px',
              backgroundRepeat: 'no-repeat',
              border: '1px solid #E4ECF6',
              boxShadow: 'inset 0 -1px 0 0 #F0F2F5',
            }}
          >
            <div
              data-sid="rmd-modelabel-13"
              className="mb-2"
              style={{ fontWeight: 500, fontSize: 14, color: 'var(--fett-text-primary)' }}
            >
              {t('scene.aiAgentConfig.receptionModeDialog.label.selectReceptionMode')}
            </div>
            <Form layout="vertical">
              <FormField<ApiReceptionMode> name="mode">
                {({ field, helpers }) => (
                  <Radio.Group
                    data-sid="rmd-modegroup-14"
                    value={field.value}
                    onChange={field.onChange}
                    className="w-full"
                  >
                    <div className="flex flex-col gap-2">
                      {receptionModeOptions.map((option) => {
                        const isSelected = field.value === option.value;
                        const isDisabled = Boolean(disableReplyModes && isReplyMode(option.value));
                        const showReplyLogic =
                          isSelected &&
                          [
                            'AI_REPLY',
                            'AI_REPLY_AND_HUMAN',
                            'AI_REPLY_AND_LOGISTICS',
                            'AI_REPLY_AND_LOGISTICS_HUMAN',
                            'AI_REPLY_AND_TODO',
                            'AI_REPLY_AND_HUMAN_TODO',
                          ].includes(option.value);
                        const showTodoSelect = isSelected && needTodoType(option.value);
                        const isTodoRequired = needTodoType(option.value);
                        const optionNode = (
                          <div
                            key={option.value}
                            data-sid="rmd-modeoption-15"
                            className={`rounded bg-white p-3 transition-colors ${
                              isSelected
                                ? 'border border-solid border-[var(--fett-scenario-accent-text)]'
                                : 'border border-solid border-[var(--fett-border-default)]'
                            } ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            role="radio"
                            tabIndex={0}
                            aria-checked={isSelected}
                            aria-disabled={isDisabled}
                            onClick={() => {
                              if (isDisabled) return;
                              helpers.setValue(option.value);
                            }}
                            onKeyDown={(event) => {
                              if (isDisabled) return;
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                helpers.setValue(option.value);
                              }
                            }}
                          >
                            <Radio value={option.value} disabled={isDisabled}>
                              <span className="text-sm text-[var(--fett-text-primary)]">
                                {option.label}
                              </span>
                              {option.hint && (
                                <span className="text-xs text-[var(--fett-text-tertiary)] ml-1">
                                  {option.hint}
                                </span>
                              )}
                            </Radio>

                            {/* 回复逻辑区域 */}
                            {showReplyLogic && (shouldShowReplyTitle || shouldShowReplyContent) && (
                              <div data-sid="rmd-replydetail-16" className="mt-3 ml-6 flex">
                                <div
                                  data-sid="rmd-replylabel-26"
                                  className="w-20 flex-shrink-0"
                                  style={{
                                    fontWeight: 400,
                                    fontSize: 12,
                                    color: 'var(--fett-text-tertiary)',
                                    lineHeight: '32px',
                                  }}
                                >
                                  {t('scene.aiAgentConfig.scenarioDetail.label.replyLogic')}
                                </div>
                                <div className="flex-1">
                                  {shouldShowReplyTitle ? (
                                    <div
                                      className="mb-2 inline-flex items-center px-2"
                                      style={{
                                        height: 32,
                                        background: 'var(--fett-erp-card-bg)',
                                        borderRadius: 4,
                                        fontWeight: 500,
                                        fontSize: 14,
                                        color: 'var(--fett-text-primary)',
                                      }}
                                    >
                                      {normalizedReplyLogicDesc}
                                    </div>
                                  ) : null}
                                  {shouldShowReplyContent ? (
                                    <div
                                      data-sid="rmd-replybox-17"
                                      className="rounded bg-[var(--fett-bg-info-tip)] p-[12px]"
                                    >
                                      {isFixedReply ? (
                                        <>
                                          <div className="flex items-center gap-1 mb-2">
                                            <LockKeyhole
                                              size={14}
                                              className="text-[var(--fett-text-primary)]"
                                            />
                                            <span className="text-xs text-[var(--fett-text-primary)]">
                                              {t(
                                                'scene.aiAgentConfig.scenarioDetail.label.presetReply',
                                              )}
                                            </span>
                                          </div>
                                          <div
                                            className="pl-5 text-sm"
                                            style={{
                                              color: 'var(--fett-text-secondary)',
                                              lineHeight: '20px',
                                            }}
                                          >
                                            <TemplateText
                                              text={normalizedReplyContent}
                                              parameterMap={parameterMap}
                                            />
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-1 mb-2">
                                            <Icon component={MessageSvg} style={{ fontSize: 16 }} />
                                            <span
                                              style={{
                                                fontWeight: 500,
                                                fontSize: 12,
                                                color: 'var(--fett-text-primary)',
                                                lineHeight: '18px',
                                              }}
                                            >
                                              {t(
                                                'scene.aiAgentConfig.scenarioDetail.label.replyExample',
                                              )}
                                            </span>
                                            <span
                                              style={{
                                                fontWeight: 400,
                                                fontSize: 12,
                                                color: 'var(--fett-text-quaternary)',
                                                lineHeight: '18px',
                                              }}
                                            >
                                              {t(
                                                'scene.aiAgentConfig.scenarioDetail.description.replyExampleGenerated',
                                              )}
                                            </span>
                                          </div>
                                          <div
                                            className="pl-5 text-sm"
                                            style={{
                                              color: 'var(--fett-text-secondary)',
                                              lineHeight: '20px',
                                            }}
                                          >
                                            <TemplateText
                                              text={normalizedReplyContent}
                                              parameterMap={parameterMap}
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            )}

                            {/* 关联待办项 */}
                            {showTodoSelect && (
                              <div
                                data-sid="rmd-todoselect-19"
                                className="mt-3 ml-6 rounded bg-[var(--fett-bg-info-tip)] p-[12px]"
                              >
                                <FormField<string>
                                  name="todoType"
                                  style={{ marginBottom: 0 }}
                                  label={
                                    <span
                                      className="inline-flex items-center gap-1"
                                      style={{
                                        fontWeight: 500,
                                        fontSize: 14,
                                        color: 'var(--fett-text-primary)',
                                      }}
                                    >
                                      <Icon
                                        component={TodoSvg}
                                        style={{ fontSize: 16, color: 'var(--fett-text-primary)' }}
                                      />
                                      {t(
                                        'scene.aiAgentConfig.receptionModeDialog.label.relatedTask',
                                      )}
                                      {isTodoRequired && (
                                        <span className="ml-0.5" style={{ color: '#FF4D4F' }}>
                                          *
                                        </span>
                                      )}
                                    </span>
                                  }
                                  validate={
                                    isTodoRequired
                                      ? (value) =>
                                          !value
                                            ? t(
                                                'scene.aiAgentConfig.receptionModeDialog.validation.relatedTaskRequired',
                                              )
                                            : undefined
                                      : undefined
                                  }
                                >
                                  {({ field: todoField }) => (
                                    <Select
                                      data-sid="rmd-todoselect-input-20"
                                      placeholder={t(
                                        'scene.aiAgentConfig.receptionModeDialog.placeholder.selectRelatedTask',
                                      )}
                                      value={todoField.value}
                                      onChange={todoField.onChange}
                                      options={todoOptions}
                                      className="w-full"
                                      loading={todoPolicyLoading}
                                      notFoundContent={
                                        todoPolicyLoading ? <Spin size="small" /> : null
                                      }
                                      allowClear={!isTodoRequired}
                                      showSearch
                                      filterOption={(input, option) =>
                                        (option?.label as string)
                                          ?.toLowerCase()
                                          .includes(input.toLowerCase()) ?? false
                                      }
                                    />
                                  )}
                                </FormField>
                              </div>
                            )}
                          </div>
                        );

                        return isDisabled ? (
                          <Tooltip
                            key={option.value}
                            title={t(
                              'scene.aiAgentConfig.receptionModeDialog.tooltip.unsupportedOption',
                            )}
                          >
                            {optionNode}
                          </Tooltip>
                        ) : (
                          optionNode
                        );
                      })}
                    </div>
                  </Radio.Group>
                )}
              </FormField>
            </Form>
          </div>
        </Drawer>
      )}
    </Formik>
  );
}

const drawerStyle = css`
  .ant-drawer-body {
    background: var(--fett-erp-card-bg);
    padding: 16px;
  }

  .ant-drawer-footer {
    padding: 12px 24px;
    border-top: 1px solid var(--fett-border-default);
  }
`;

const ConfirmContent = styled.div`
  color: var(--fett-text-secondary);
  font-size: 14px;
  line-height: 20px;

  .storelist-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;

const ConfirmStoreMoreTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  vertical-align: text-bottom;

  > img {
    display: block;
    width: 16px;
    height: 16px;
  }
`;

const ConfirmStoreList = styled.ul`
  max-width: 325px;
  max-height: 200px;
  margin: -12px -16px;
  padding: 12px 16px;
  list-style: none;
  overflow-x: hidden;
  overflow-y: auto;

  > li {
    line-height: 30px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export default ReceptionModeDrawer;
